import tierGold from "@/assets/tier-gold.png";
import tierSilver from "@/assets/tier-silver.png";
import tierBronze from "@/assets/tier-bronze.png";
import tierPlatinum from "@/assets/tier-platinum.png";
import tierDiamond from "@/assets/tier-diamond.png";

/**
 * The loyalty tier ladder, in rank order (lowest first).
 *
 * This is the only place tier names, colours, badges, thresholds and raffle
 * entitlements are defined. Everything else — the Header/MobileBottomNav chips,
 * the Giveaways and SponsoredBattles badges, the Admin table/pie/pickers, the
 * Profile cards, the Tiers ladder and the Store gate — reads from here.
 * Adding a tier is a single entry.
 *
 * `key` must match the Postgres `user_tier` enum exactly.
 *
 * Rank is set by **all-time weighted wager**. Rainbet applies the RTP
 * weighting upstream, so the figure their affiliate API returns already is
 * the weighted number — nothing here recomputes it.
 */

export type TierKey = "unranked" | "bronze" | "silver" | "gold" | "platinum" | "diamond";

/** Which weekly raffle a tier's free entries apply to. */
export type RaffleKey = "raffle1" | "raffle2";

export interface Tier {
  key: TierKey;
  /** Uppercase display name, e.g. "GOLD". */
  label: string;
  /** Title-case name with its medal, for pickers. */
  pickerLabel: string;
  /** Single-letter rank mark for the ladder rows. */
  mark: string;
  /** Badge art. Unranked has none — callers fall back to a user icon. */
  image: string | null;
  /** Minimum all-time weighted wager, in whole dollars. */
  threshold: number;
  /** Threshold rendered for display, e.g. "$50,000". */
  thresholdLabel: string;
  /** Weekly raffle the free entries apply to, if any. */
  raffle: RaffleKey | null;
  /** Number of free entries per weekly draw. */
  entries: number;
  /** Extra perk copy shown under the tier name. */
  perk: string;
  /** Small chip: background + text, no border. */
  chip: string;
  /** Border to add when the chip is outlined (Giveaways, SponsoredBattles). */
  chipBorder: string;
  /** Card border + background (Profile, Tiers). */
  cardBorder: string;
  cardBg: string;
  /** Text colour on its own. */
  text: string;
  /** Hex for the Admin distribution chart and the ladder rank rail. */
  chart: string;
  /** Requirement copy, long form. */
  requirement: string;
  /** Requirement copy for tight grids. */
  requirementShort: string;
}

export const TIERS: Tier[] = [
  {
    key: "unranked",
    label: "UNRANKED",
    pickerLabel: "Unranked",
    mark: "—",
    image: null,
    threshold: 0,
    thresholdLabel: "$0",
    raffle: null,
    entries: 0,
    perk: "Wager to rank up — your first tier unlocks at $1,000 all-time",
    chip: "bg-muted/20 text-muted-foreground",
    chipBorder: "border-muted/50",
    cardBorder: "border-border",
    cardBg: "bg-muted/30",
    text: "text-muted-foreground",
    chart: "#475569",
    requirement: "Start wagering to rank up",
    requirementShort: "Start wagering",
  },
  {
    key: "bronze",
    label: "BRONZE",
    pickerLabel: "🥉 Bronze",
    mark: "B",
    image: tierBronze,
    threshold: 1_000,
    thresholdLabel: "$1,000",
    raffle: "raffle1",
    entries: 1,
    perk: "1 free entry into every weekly Raffle 1 draw",
    chip: "bg-orange-700/20 text-orange-700",
    chipBorder: "border-orange-700/50",
    cardBorder: "border-orange-600/50",
    cardBg: "bg-orange-600/10",
    text: "text-orange-600",
    chart: "#B45309",
    requirement: "$1,000 all-time weighted wager",
    requirementShort: "$1,000 all-time",
  },
  {
    key: "silver",
    label: "SILVER",
    pickerLabel: "🥈 Silver",
    mark: "S",
    image: tierSilver,
    threshold: 25_000,
    thresholdLabel: "$25,000",
    raffle: "raffle1",
    entries: 3,
    perk: "3 free entries into every weekly Raffle 1 draw",
    chip: "bg-gray-400/20 text-gray-400",
    chipBorder: "border-gray-400/50",
    cardBorder: "border-slate-400/50",
    cardBg: "bg-slate-400/10",
    text: "text-slate-400",
    chart: "#94A3B8",
    requirement: "$25,000 all-time weighted wager",
    requirementShort: "$25,000 all-time",
  },
  {
    key: "gold",
    label: "GOLD",
    pickerLabel: "🥇 Gold",
    mark: "G",
    image: tierGold,
    threshold: 100_000,
    thresholdLabel: "$100,000",
    raffle: "raffle2",
    entries: 1,
    perk: "1 free entry into every weekly Raffle 2 draw",
    chip: "bg-yellow-500/20 text-yellow-500",
    chipBorder: "border-yellow-500/50",
    cardBorder: "border-yellow-500/50",
    cardBg: "bg-yellow-500/10",
    text: "text-yellow-500",
    chart: "#EAB308",
    requirement: "$100,000 all-time weighted wager",
    requirementShort: "$100,000 all-time",
  },
  {
    key: "platinum",
    label: "PLATINUM",
    pickerLabel: "💠 Platinum",
    mark: "P",
    image: tierPlatinum,
    threshold: 500_000,
    thresholdLabel: "$500,000",
    raffle: "raffle2",
    entries: 1,
    perk: "1 free entry into every weekly Raffle 2 draw",
    chip: "bg-cyan-300/20 text-cyan-300",
    chipBorder: "border-cyan-300/50",
    cardBorder: "border-cyan-300/50",
    cardBg: "bg-cyan-300/10",
    text: "text-cyan-300",
    chart: "#67E8F9",
    requirement: "$500,000 all-time weighted wager",
    requirementShort: "$500,000 all-time",
  },
  {
    key: "diamond",
    label: "DIAMOND",
    pickerLabel: "💎 Diamond",
    mark: "D",
    image: tierDiamond,
    threshold: 2_500_000,
    thresholdLabel: "$2,500,000",
    raffle: "raffle2",
    entries: 1,
    perk: "1 free entry into every weekly Raffle 2 draw, plus direct contact with the team",
    chip: "bg-violet-400/20 text-violet-400",
    chipBorder: "border-violet-400/50",
    cardBorder: "border-violet-400/50",
    cardBg: "bg-violet-400/10",
    text: "text-violet-400",
    chart: "#A78BFA",
    requirement: "$2,500,000 all-time weighted wager",
    requirementShort: "$2,500,000 all-time",
  },
];

/** The two weekly raffles the tier entries feed. Tickets are also buyable in the Store. */
export const RAFFLES: Record<RaffleKey, { name: string; prize: string; ticketPoints: number }> = {
  raffle1: { name: "Raffle 1", prize: "$40 bonus buy, slot of your choice", ticketPoints: 200 },
  raffle2: { name: "Raffle 2", prize: "$100 bonus buy", ticketPoints: 600 },
};

/** Every tier key, rank order. */
export const TIER_KEYS = TIERS.map((t) => t.key);

/** Ranked tiers only — excludes `unranked`. Used by the tier-restriction pickers. */
export const RANKED_TIERS = TIERS.filter((t) => t.key !== "unranked");

const BY_KEY = new Map<string, Tier>(TIERS.map((t) => [t.key, t]));

/** Look up a tier, falling back to Unranked for null/unknown values. */
export const tierOf = (key: string | null | undefined): Tier =>
  BY_KEY.get((key ?? "").toLowerCase()) ?? TIERS[0];

/** Chip classes for a tier, optionally outlined. */
export const tierChip = (key: string | null | undefined, withBorder = false): string => {
  const t = tierOf(key);
  return withBorder ? `${t.chip} ${t.chipBorder}` : t.chip;
};

/** Rank index — higher is better. Unranked is 0. */
export const tierRank = (key: string | null | undefined): number =>
  TIERS.findIndex((t) => t.key === tierOf(key).key);

/**
 * Does `key` satisfy a `required_tiers` list?
 *
 * Membership is exact, matching how giveaways have always worked — a Gold-only
 * giveaway does not admit Platinum unless Platinum is listed. Unranked never
 * satisfies a restriction.
 */
export const meetsTierRequirement = (
  key: string | null | undefined,
  required: readonly string[] | null | undefined,
): boolean => {
  if (!required || required.length === 0) return true;
  const k = tierOf(key).key;
  return k !== "unranked" && required.includes(k);
};

/** The tier an all-time weighted wager earns. */
export const tierForWager = (wager: number): Tier => {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (wager >= TIERS[i].threshold) return TIERS[i];
  }
  return TIERS[0];
};

/** Highest-first, for ladder displays that lead with the top rank. */
export const TIERS_DESC = [...TIERS].reverse();
